import { logger } from '../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BattleState,
  BattleUnitState,
  TurnOrderEntry,
  HotbarSkill,
  VictoryData,
} from '../types/battle';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { isUnitStunned, getUnitAtPosition, calculateWeaponRange, calculateSkillNodeTargets, canCastAnySkill, getCurrentTurnUnit } from '../utils/battleUtils';
import BattleEffects from './BattleEffects';
import BattleFx from './BattleFx';
import BattleVictory from './BattleVictory';
import BattleLegendPanel from './BattleLegendPanel';
import BattleLogPanel from './BattleLogPanel';
import BattleUnitInfoPanel from './BattleUnitInfoPanel';
import BattleTurnOrderPanel from './BattleTurnOrderPanel';
import BattleSidebarActions from './BattleSidebarActions';
import BattleActionsBar from './BattleActionsBar';
import BattleGridTiles from './BattleGridTiles';
import { useBattleLog } from '../hooks/useBattleLog';
import { useGridControls } from '../hooks/useGridControls';
import { useBattleEffects } from '../hooks/useBattleEffects';
import { useBattleAnimation } from '../hooks/useBattleAnimation';
import { useBattleModes } from '../hooks/useBattleModes';
import { useBattleActions } from '../hooks/useBattleActions';
import { useConfirm } from './ConfirmModal';
import { makeAuthenticatedRequest } from '../utils/api';


interface BattleGridProps {
  battleId: number;
  onExitBattle: () => void;
}


const BattleGrid: React.FC<BattleGridProps> = ({ battleId, onExitBattle }) => {
  const { handleAuthError, user } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial, isActive: isTutorialActive } = useTutorial();
  const isTutorialActiveRef = useRef(isTutorialActive);
  useEffect(() => { isTutorialActiveRef.current = isTutorialActive; }, [isTutorialActive]);
  const [battle, setBattle] = useState<BattleState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<BattleUnitState | null>(null);
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [victoryData, setVictoryData] = useState<VictoryData>({ experience: 0, loot: [], tilesUnlocked: [] });
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(60);
  const turnTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { battleLog, setBattleLog, battleLogFilter, setBattleLogFilter, logContentRef, addToBattleLog, addEnhancedBattleLog } = useBattleLog();
  const { confirm, confirmDialog } = useConfirm();

  const {
    floatingTexts,
    passiveNotifications,
    fxEntries,
    addFloatingText,
    addPassiveNotification,
    removeFloatingText,
    removePassiveNotification,
    removeFxEntry,
    spawnFx,
    showTurnTransition,
    processPassiveEvents,
  } = useBattleEffects();

  const { processAIActions, animateUnitMovement } = useBattleAnimation(
    setBattle, addEnhancedBattleLog, addToBattleLog, addNotification,
    addFloatingText, spawnFx
  );

  // Stable ref so useBattleModes can call executeSkillDirect without a circular dep
  const executeSkillDirectRef = useRef<(skill: HotbarSkill, x: number, y: number) => Promise<void>>(async () => {});

  const {
    movementMode, attackMode, skillMode,
    validMoves, validTargets, weaponRange,
    skillTargets, skillRange, aoeArea, selectedSkill,
    itemMode, selectedItem, itemTargets,
    setMovementMode, setAttackMode, setSkillMode,
    setValidMoves, setValidTargets, setWeaponRange,
    setSkillTargets, setSkillRange, setAoeArea, setSelectedSkill,
    clearAllModes, clearSkillState, clearSelectionState,
    enterMovementMode, enterAttackMode,
    cancelMovement, cancelAttack, cancelSkill,
    selectSkillNode, cancelSkillNode,
    selectBattleItem, cancelItem,
  } = useBattleModes(
    battle, selectedUnit, battleId,
    setSelectedUnit, addEnhancedBattleLog, setActionError,
    (...args) => executeSkillDirectRef.current(...args)
  );

  const {
    fetchBattleState,
    moveUnit,
    attackUnit,
    executeSkill,
    executeSkillDirect,
    consumeItem,
    endTurn,
    handleFleeBattle,
    equippedSkills,
    battleItems,
  } = useBattleActions({
    battleId, battle, selectedUnit, selectedSkill, selectedItem,
    setBattle, setSelectedUnit, setLoading, setError, setActionError,
    setShowVictoryScreen, setVictoryData,
    battleLog, setBattleLog, addToBattleLog, addEnhancedBattleLog,
    addFloatingText, spawnFx, processPassiveEvents, processAIActions, showTurnTransition,
    clearAllModes, clearSkillState, cancelMovement, cancelAttack, cancelSkill, cancelItem,
    handleAuthError, addNotification, onExitBattle, isTutorialActiveRef, confirm,
  });

  // Keep ref in sync so useBattleModes.selectSkillNode always calls the latest version
  executeSkillDirectRef.current = executeSkillDirect;

  const {
    gridViewportSize,
    zoomLevel,
    panOffset,
    setPanOffset,
    isDragging,
    gridContainerRef,
    autoFit,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleResizeStart,
  } = useGridControls();

  // Auto-fit grid to container once when battle data first loads
  const hasAutoFitted = useRef(false);
  useEffect(() => {
    if (battle && !hasAutoFitted.current) {
      hasAutoFitted.current = true;
      requestAnimationFrame(() => autoFit(battle.gridWidth ?? 10, battle.gridHeight ?? 10));
    }
  }, [battle]);

  // Sync selectedUnit with latest battle state to include status effects and cooldowns
  useEffect(() => {
    if (battle && selectedUnit) {
      const updatedUnit = battle.units?.find(u => u.id === selectedUnit.id);
      if (updatedUnit) setSelectedUnit(updatedUnit);
    }
  }, [battle]);

  // Clear action errors when turn changes
  useEffect(() => {
    if (battle?.currentUnitIndex !== undefined) setActionError(null);
  }, [battle?.currentUnitIndex]);

  // Clear localStorage and show victory screen when battle ends
  useEffect(() => {
    if (battle && (battle.status === 'Victory' || battle.status === 'Defeat')) {
      try {
        localStorage.removeItem(`battle_${battleId}_state`);
      } catch (err) {
        logger.warn('Failed to clear battle state:', err);
      }
      if (battle.status === 'Victory' && !showVictoryScreen) {
        setVictoryData({
          experience: 0,
          loot: battle.materialsAwarded || [],
          tilesUnlocked: battle.tilesUnlocked || [],
          characterXP: battle.characterXP || [],
        });
        setShowVictoryScreen(true);
      }
    }
  }, [battle, battleId, showVictoryScreen]);

  useEffect(() => {
    fetchBattleState();
  }, [battleId, fetchBattleState]);

  useEffect(() => {
    if (battle && !loading) checkAndStartTutorial('battle-basics');
  }, [battle, loading, checkAndStartTutorial]);

  // Turn timer countdown
  useEffect(() => {
    setTurnTimeRemaining(60);
    if (turnTimerRef.current) {
      clearInterval(turnTimerRef.current);
      turnTimerRef.current = null;
    }
    if (battle?.status !== 'InProgress') return;

    turnTimerRef.current = setInterval(() => {
      setTurnTimeRemaining(prev => {
        if (isTutorialActiveRef.current) return prev;
        if (prev <= 1) {
          if (battle && currentTurnUnit) {
            addToBattleLog(`⏰ Time's up for ${currentTurnUnit.name}! Ending turn automatically.`);
            endTurn(currentTurnUnit).catch(err => logger.error('Error auto-ending turn:', err));
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current);
        turnTimerRef.current = null;
      }
    };
  }, [battle?.currentUnitIndex, battle?.status]);

  // Keyboard controls for grid panning and action hotkeys
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

      const panSpeed = 50;
      if (event.key === 'ArrowUp') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x, y: prev.y + panSpeed })); return; }
      if (event.key === 'ArrowDown') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x, y: prev.y - panSpeed })); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x + panSpeed, y: prev.y })); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); setPanOffset(prev => ({ x: prev.x - panSpeed, y: prev.y })); return; }

      if (!selectedUnit?.isPlayerUnit) return;

      const key = event.key;
      if (key === '`' || key === '~') {
        event.preventDefault();
        const weaponAPCost = selectedUnit.equipment?.weapon?.weaponAPCost || 2;
        if (selectedUnit.equipment?.weapon && !attackMode && selectedUnit.stats.currentAP >= weaponAPCost) {
          enterAttackMode();
        }
        return;
      }

      let skillSlotIndex = -1;
      if (key >= '1' && key <= '9') skillSlotIndex = parseInt(key) - 1;
      else if (key === '0') skillSlotIndex = 9;
      if (skillSlotIndex === -1) return;

      event.preventDefault();
      const equippedSkill = equippedSkills.find(es => es.hotbarSlot === skillSlotIndex);
      if (equippedSkill) {
        const skill = equippedSkill.skill;
        const cooldownInfo = selectedUnit.skillCooldowns?.find(cd => cd.skillNodeId === Number(skill.id));
        const isOnCooldown = cooldownInfo && cooldownInfo.turnsRemaining > 0;
        if (!isOnCooldown && !isUnitStunned(selectedUnit) && selectedUnit.stats.currentAP >= (skill.apCost || 0)) {
          selectSkillNode(equippedSkill);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedUnit, equippedSkills, attackMode, skillMode, panOffset]);

  const handleTileClick = (x: number, y: number) => {
    if (battle?.status === 'Victory' || battle?.status === 'Defeat') return;
    if (movementMode && selectedUnit) {
      if (validMoves.some(m => m.x === x && m.y === y)) moveUnit(selectedUnit.id, x, y);
      return;
    }
    if (attackMode && selectedUnit) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (target && validTargets.some(t => t.id === target.id)) attackUnit(selectedUnit.id, target.id);
      return;
    }
    if (skillMode && selectedSkill && selectedUnit) {
      if (skillTargets.some(t => t.x === x && t.y === y)) executeSkill(x, y);
      return;
    }
    if (skillMode && !selectedSkill) {
      setActionError('Please select a skill first, or click Cancel Skill to exit skill mode.');
      return;
    }
    if (itemMode && selectedItem) {
      const target = getUnitAtPosition(battle?.units ?? [], x, y);
      if (target && itemTargets.some(t => t.id === target.id)) consumeItem(target.id);
      return;
    }

    const unit = getUnitAtPosition(battle?.units ?? [], x, y);
    if (!unit) { clearSelectionState(null); return; }

    const ctu = getCurrentTurnUnit(battle);
    if (unit.isPlayerUnit && ctu && ctu.unitId === unit.id) {
      clearSelectionState(unit);
      setActionError(null);
    } else if (unit.isPlayerUnit && ctu && ctu.unitId !== unit.id) {
      addEnhancedBattleLog('error', { message: `❌ It's ${ctu.name}'s turn, not ${unit.name}'s turn!` });
      setActionError(`It's ${ctu.name}'s turn. You can only control the active unit.`);
    } else if (!unit.isPlayerUnit) {
      clearSelectionState(unit);
    }
  };

  if (loading) return <div className="battle-loading">Loading battle...</div>;
  if (error) return (
    <div className="battle-error">
      <p>Error: {error}</p>
      <button onClick={onExitBattle}>Return to World Map</button>
    </div>
  );
  if (!battle) return (
    <div className="battle-error">
      <p>Battle not found</p>
      <button onClick={onExitBattle}>Return to World Map</button>
    </div>
  );

  const currentTurnUnit = getCurrentTurnUnit(battle);
  const displayUnit = selectedUnit || (currentTurnUnit ? battle.units?.find(u => u.id === currentTurnUnit.unitId) : null);
  const isDisplayUnitCurrentTurn = !selectedUnit && !!currentTurnUnit;

  return (
    <div className="battle-screen">
      {confirmDialog}

      {showVictoryScreen && battle.status === 'Victory' && (
        <BattleVictory
          experience={victoryData.experience}
          loot={victoryData.loot.map(m => ({ name: m.materialName, quantity: m.quantity, rarity: m.rarity || 'Common' }))}
          tilesUnlocked={victoryData.tilesUnlocked}
          bonuses={victoryData.bonuses}
          characterXP={victoryData.characterXP}
          onContinue={onExitBattle}
        />
      )}

      {battle.status === 'Defeat' && (
        <div className="battle-end-banner defeat" style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', zIndex: 1000,
          backgroundColor: '#f44336', color: 'white',
          padding: '20px 40px', borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          textAlign: 'center', fontSize: '24px', fontWeight: 'bold'
        }}>
          💀 DEFEAT!
          <div style={{ fontSize: '16px', marginTop: '10px' }}>All units defeated!</div>
          <button onClick={onExitBattle} style={{
            marginTop: '15px', padding: '10px 20px', fontSize: '16px',
            backgroundColor: 'white', color: '#f44336',
            border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'
          }}>
            Return to World Map
          </button>
        </div>
      )}

      <div className="battle-left-section">
      <div className="battle-content">
        <div className="battle-left-column">

        {/* TOP BAR: battle location + turn order */}
        <div className="battle-top-bar">
          <h4 className="battle-top-title">{battle.battleName}</h4>
          <BattleTurnOrderPanel
            turnOrder={battle.turnOrder ?? []}
            units={battle.units ?? []}
            currentTurnUnit={currentTurnUnit}
            attackMode={attackMode}
            skillMode={skillMode}
            validTargets={validTargets}
            skillTargets={skillTargets}
            selectedUnit={selectedUnit}
            selectedSkill={selectedSkill}
            battleStatus={battle.status}
            turnTimeRemaining={turnTimeRemaining}
            showTimer={false}
            onAttackUnit={attackUnit}
            onExecuteSkill={executeSkill}
            onSelectUnit={(unit) => {
              setSelectedUnit(unit);
              setMovementMode(false);
              setAttackMode(false);
              setSkillMode(false);
              setValidMoves([]);
              setValidTargets([]);
              setSkillTargets([]);
              setWeaponRange([]);
              setSkillRange([]);
            }}
          />
        </div>

        {/* GRID */}
        <div className="battle-grid-wrapper">
          <div
            className="battle-grid-container"
            ref={gridContainerRef}
            style={{
              position: 'relative',
              overflow: 'hidden',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div className="grid-zoom-controls">
              <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out (-)">-</button>
              <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
              <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In (+)">+</button>
              <button className="zoom-btn reset" onClick={() => handleResetZoom(battle?.gridWidth ?? 10, battle?.gridHeight ?? 10)} title="Reset View">⟲</button>
            </div>

            <div
              className="battle-grid"
              style={{
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              <BattleGridTiles
                gridWidth={battle.gridWidth}
                gridHeight={battle.gridHeight}
                tiles={battle.tiles}
                units={battle.units}
                selectedUnit={selectedUnit}
                currentTurnUnit={currentTurnUnit}
                validMoves={validMoves}
                validTargets={validTargets}
                skillTargets={skillTargets}
                skillRange={skillRange}
                aoeArea={aoeArea}
                weaponRange={weaponRange}
                attackMode={attackMode}
                skillMode={skillMode}
                selectedSkill={selectedSkill}
                itemTargets={itemTargets}
                onTileClick={handleTileClick}
                onAoeAreaChange={setAoeArea}
              />
            </div>

            <BattleEffects
              floatingTexts={floatingTexts}
              passiveNotifications={passiveNotifications}
              onFloatingTextComplete={removeFloatingText}
              onPassiveNotificationComplete={removePassiveNotification}
            />
            <BattleFx
              entries={fxEntries}
              tileSize={80}
              onComplete={removeFxEntry}
            />
          </div>
        </div>

        {/* ACTION AREA: skill hotbar (left) + timer/flee/legend stack (right) */}
        <div className="battle-action-area">
          <div className="battle-action-hotbar">
            {selectedUnit?.isPlayerUnit && (
              <BattleActionsBar
                selectedUnit={selectedUnit}
                currentTurnUnit={currentTurnUnit}
                attackMode={attackMode}
                skillMode={skillMode}
                selectedSkill={selectedSkill}
                equippedSkills={equippedSkills}
                onEnterAttackMode={enterAttackMode}
                onSelectSkillNode={selectSkillNode}
                onShowWeaponRange={(unit) => setWeaponRange(battle ? calculateWeaponRange(unit, battle) : [])}
                onClearWeaponRange={() => setWeaponRange([])}
                onShowSkillRange={(skill) => { if (selectedUnit && battle) { const { rangePositions } = calculateSkillNodeTargets(skill, selectedUnit, battle); setSkillRange(rangePositions); } }}
                onClearSkillRange={() => setSkillRange([])}
              />
            )}
          </div>
          <div className="battle-action-utility">
            {battle.status === 'InProgress' && currentTurnUnit && (
              <div className={`turn-timer ${turnTimeRemaining <= 10 ? 'timer-warning' : ''}`}>
                <span className="timer-icon">⏱️</span>
                <span className="timer-text">{turnTimeRemaining}s</span>
              </div>
            )}
            <button onClick={handleFleeBattle} className="exit-battle-btn">Flee</button>
            <button
              onClick={() => setShowLegend(!showLegend)}
              className="legend-toggle-btn"
              title="Show/hide tile color legend"
            >
              {showLegend ? 'Hide Legend' : 'Legend'}
            </button>
          </div>
        </div>
        </div>{/* end battle-left-column */}
      </div>{/* end battle-content */}
      </div>{/* end battle-left-section */}

        {/* RIGHT PANEL: unit info + actions + log */}
        <div className="battle-right-panel">
          {displayUnit && (
            <BattleUnitInfoPanel displayUnit={displayUnit} isCurrentTurn={isDisplayUnitCurrentTurn} />
          )}

          <BattleSidebarActions
            movementMode={movementMode}
            attackMode={attackMode}
            skillMode={skillMode}
            itemMode={itemMode}
            selectedUnit={selectedUnit}
            currentTurnUnit={currentTurnUnit}
            battleStatus={battle.status}
            selectedSkill={selectedSkill}
            selectedItem={selectedItem}
            battleItems={battleItems}
            actionError={actionError}
            onCancelMovement={cancelMovement}
            onCancelAttack={cancelAttack}
            onCancelSkill={cancelSkill}
            onCancelItem={cancelItem}
            onEnterMovementMode={enterMovementMode}
            onEnterAttackMode={enterAttackMode}
            onSelectBattleItem={selectBattleItem}
            onEndTurn={endTurn}
            onClearActionError={() => setActionError(null)}
            onInstantWin={user?.role !== 'Admin' ? undefined : async () => {
              try {
                const response = await makeAuthenticatedRequest(`/battles/${battleId}/instant-win`, { method: 'POST' });
                if (response.ok) {
                  const result = await response.json();
                  setBattle(result.battleState);
                  setVictoryData({ experience: result.experience || 0, loot: result.materialsAwarded || [], tilesUnlocked: result.tilesUnlocked || [], bonuses: result.bonuses || null });
                  setShowVictoryScreen(true);
                  addEnhancedBattleLog('victory', {});
                }
              } catch (err) {
                logger.error('Instant win failed:', err);
              }
            }}
          />

          <BattleLogPanel
            entries={battleLog}
            filter={battleLogFilter}
            onFilterChange={setBattleLogFilter}
            logContentRef={logContentRef}
          />
        </div>

      {showLegend && <BattleLegendPanel onClose={() => setShowLegend(false)} />}
    </div>
  );
};

export default BattleGrid;
